/**
 * CallHandoffPage
 * Landing page an agent reaches by tapping YES in the handoff text.
 *
 * SPEC-2300: loading this page no longer holds the lead — the row stays
 * `pending` for its full 72-hour window, so there is no countdown and no
 * expiry redirect. The claim happens when the agent picks Call lead or Text
 * lead, confirms in the modal, and the row moves pending -> claimed.
 *
 * Layout is phone-first: the page is opened from a text message, so the actions
 * sit in a bar pinned to the bottom of the viewport and the reading material
 * scrolls behind it.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@crisellevaldez/speculo-ui';
import { Contact } from '../../types/contact.types';
import { useContactsByEmail } from '../../hooks/useContactsByEmail';
import { AskRemiButton } from '../../components/features/contacts/AskRemiButton';
import { CheckCircle2, MessageSquare, Phone } from 'lucide-react';
import { useSmsHandoff } from '../../hooks/handoff/useSmsHandoff';
import {
  HandoffCard,
  HandoffPageShell,
  HandoffSectionLabel,
} from '../../components/features/handoff/HandoffPageShell';
import { FullPageLoader } from '../../components/common/FullPageLoader';
import { cn } from '../../utils/cn';
import { formatPhoneNumber } from '../../utils/formatters';
import { getHotnessBadge } from '../../utils/handoffHotness';
import { signalFlagsFrom } from '../../utils/handoffFormat';
import {
  HandoffLocationLabel,
  HandoffMetaLine,
} from '../../components/features/handoff/HandoffMetaLine';

/** Calling and texting are utilities here; claiming is the commitment. */
type DeepLinkIntent = 'call' | 'text';

const CallHandoffPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();


  const {
    handoffDetails,
    isLoading: handoffLoading,
    error: handoffError,
    handleClaim,
    isClaiming,
  } = useSmsHandoff({
    token: token || undefined,
    onError: (error) => {
      showToast({
        message: error || 'Handoff error occurred',
        variant: 'error',
      });
    },
  });

  const { getContactsByEmail, loading } = useContactsByEmail();
  const [contact, setContact] = useState<Contact | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Check if this is test mode
  const isTestMode = token === 'test';

  // Mock contact for test mode
  const mockContact: Contact = {
    id: 'test-contact-id',
    organization_id: 'test-org',
    firstName: 'John',
    lastName: 'Doe',
    phoneNumber: '+15551234567',
    phoneStatus: 'valid',
    read: false,
    email: 'john.doe@example.com',
    address: {
      street: '123 Main St',
      city: 'Boston',
      state: 'MA',
      zip: '02101',
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    opt_in: true,
    opt_in_date: new Date().toISOString(),
    opt_in_desc: 'Test contact',
    call_tester: false,
  };

  // Fetch the full contact so Ask Remi has something to work with. Everything
  // the page must render comes from the claim-link response, so a miss here
  // only costs the Ask Remi button — it never blocks the landing page.
  if (!isInitialized && handoffDetails && !loading) {
    setIsInitialized(true);
    if (isTestMode) {
      setContact(mockContact);
    } else {
      getContactsByEmail('', '', handoffDetails.contactId)
        .then((fetchedContacts) => {
          if (fetchedContacts.length > 0) {
            setContact(fetchedContacts[0]);
          }
        })
        .catch(() => {
          // Non-fatal: the page renders from handoffDetails without Ask Remi.
        });
    }
  }

  // Handle validation failure (e.g. token already used or revoked).
  // Must check BEFORE the loading check to avoid getting stuck.
  if (!handoffLoading && (handoffError || !handoffDetails)) {
    navigate(`/handoff/error?reason=${encodeURIComponent(handoffError || 'invalid')}`);
    return null;
  }

  if (handoffLoading || !handoffDetails) {
    return <FullPageLoader bgColor="bg-transparent" dotColor="bg-amber-500" />;
  }

  const contactPhone = contact?.phoneNumber || handoffDetails.contactPhone;
  const contactName =
    contact?.firstName || contact?.lastName
      ? `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim()
      : handoffDetails.contactName || formatPhoneNumber(contactPhone) || 'Unknown Contact';

  const hotness = getHotnessBadge(handoffDetails.hotnessLevel);
  // SPEC-2330: at-a-glance signal chips, same source as the notes page + widget.
  const signalChips = signalFlagsFrom(handoffDetails.signals, handoffDetails.reasons);

  // Reaching out is a utility the agent can use before deciding, so it does not
  // claim anything on its own.
  const openDeepLink = (intent: DeepLinkIntent) => {
    if (!contactPhone) return;
    window.location.href = `${intent === 'call' ? 'tel' : 'sms'}:${contactPhone}`;
  };

  // Claims on the first tap. There is nothing to confirm — no notes to collect
  // and no destructive outcome, since a claim can be given back.
  const handleConfirmClaim = async () => {
    const claimed = await handleClaim();
    if (!claimed) return;
    navigate(`/handoff/success?type=claimed&contactId=${handoffDetails.contactId}`);
  };

  return (
    <HandoffPageShell maxWidth="max-w-md sm:max-w-lg lg:max-w-2xl">
      {/* Bottom padding clears the pinned action bar. */}
      <div className="pb-28 sm:pb-4">
        <HandoffCard>
          {/* Hero — the two things the agent needs first: who, and how hot. */}
          <div className="border-b border-foreground/10 px-5 py-5 sm:px-7 sm:py-6 lg:px-9 lg:py-8">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="truncate text-xl font-semibold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
                  {contactName}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground sm:text-base">
                  {contactPhone && (
                    <a
                      href={`tel:${contactPhone}`}
                      className="inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {formatPhoneNumber(contactPhone)}
                    </a>
                  )}
                  <HandoffLocationLabel location={handoffDetails.location} />
                </div>
              </div>
              <span
                className={cn(
                  'inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold',
                  hotness.bg,
                  hotness.text,
                  hotness.border
                )}
              >
                {hotness.icon}
                {hotness.label}
              </span>
            </div>

            <HandoffMetaLine
              channel={handoffDetails.channelOfOrigin}
              className="mt-4"
            />

            {/* Quick actions. `expandDirection` names the anchored edge, not the
                growth direction: "right" pins right:0 and grows leftward, which
                overflowed the card at the left of a full-width row. */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {contactPhone && (
                <>
                  <QuickAction
                    icon={Phone}
                    label="Call"
                    tone="emerald"
                    onClick={() => openDeepLink('call')}
                  />
                  <QuickAction
                    icon={MessageSquare}
                    label="Text"
                    tone="blue"
                    onClick={() => openDeepLink('text')}
                  />
                </>
              )}
              {contact && (
                <AskRemiButton contact={contact} expandedByDefault expandDirection="left" />
              )}
            </div>
          </div>

          {/* Reading material — hairline-separated sections, no nested panels. */}
          <div className="divide-y divide-foreground/10">
            {signalChips.length > 0 && (
              <section className="px-5 py-4 sm:px-7 sm:py-5 lg:px-9 lg:py-6">
                <HandoffSectionLabel>Signals</HandoffSectionLabel>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {signalChips.map((label) => (
                    <span
                      key={label}
                      className="inline-flex items-center rounded-md border border-foreground/15 bg-foreground/[0.04] px-2 py-0.5 text-xs font-medium text-foreground/80 sm:px-2.5 sm:py-1 sm:text-[13px]"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {handoffDetails.conversationSummary && (
              <section className="px-5 py-4 sm:px-7 sm:py-5 lg:px-9 lg:py-6">
                <HandoffSectionLabel>Conversation summary</HandoffSectionLabel>
                <p className="mt-2.5 border-l-2 border-amber-400/50 pl-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {handoffDetails.conversationSummary}
                </p>
              </section>
            )}
          </div>

          {/* Desktop CTA lives in the card; on mobile the pinned bar takes over. */}
          <div className="hidden border-t border-foreground/10 bg-foreground/[0.02] px-5 py-4 sm:block sm:px-7 lg:px-9 lg:py-5">
            <ClaimCta onClick={handleConfirmClaim} disabled={isClaiming} />
          </div>
        </HandoffCard>
      </div>

      {/* Pinned action bar — phone-first, always in thumb reach. */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-foreground/10 bg-card/95 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:hidden">
        <div className="mx-auto max-w-md">
          <ClaimCta onClick={handleConfirmClaim} disabled={isClaiming} />
        </div>
      </div>

    </HandoffPageShell>
  );
};

/** Tinted so the two channels are distinguishable at pill size. */
const QUICK_ACTION_TONES = {
  emerald:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 dark:border-emerald-400/30 dark:bg-emerald-400/15 dark:text-emerald-300 dark:hover:bg-emerald-400/25',
  blue: 'border-blue-500/30 bg-blue-500/10 text-blue-700 hover:bg-blue-500/20 dark:border-blue-400/30 dark:bg-blue-400/15 dark:text-blue-300 dark:hover:bg-blue-400/25',
} as const;

/** Small pill beside Summarize — sized to sit level with it. */
const QuickAction: React.FC<{
  icon: typeof Phone;
  label: string;
  tone: keyof typeof QUICK_ACTION_TONES;
  onClick: () => void;
}> = ({ icon: Icon, label, tone, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      'inline-flex h-6 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-medium shadow-sm transition-colors sm:h-7 sm:text-xs',
      QUICK_ACTION_TONES[tone]
    )}
  >
    <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
    {label}
  </button>
);

/**
 * The one commitment on this page. Rendered twice — in the card for desktop, in
 * the pinned bar on mobile — so it is always the obvious next step.
 */
const ClaimCta: React.FC<{
  onClick: () => void;
  disabled: boolean;
}> = ({ onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60 sm:py-3.5 sm:text-base lg:py-4 lg:text-lg',
      'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-emerald-700',
      'dark:from-emerald-600 dark:to-emerald-700 dark:text-emerald-50 dark:shadow-md dark:shadow-emerald-950/40 dark:hover:from-emerald-500 dark:hover:to-emerald-600'
    )}
  >
    <CheckCircle2 className="h-4 w-4 lg:h-5 lg:w-5" />
    Claim this lead
  </button>
);

export default CallHandoffPage;
